import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  useWindowDimensions,
} from 'react-native';
import Text from '../../components/text';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import getPlatformType from '../../helpers/helpers';
import { useIsFocused } from '@react-navigation/native';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';
import { Ionicons, MaterialCommunityIcons, Feather } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { saveUserProfile } from '../../redux/action/authActions';
import debounce from 'lodash.debounce';
import VerificationSection from '../../components/LandlordReadyness/VerificationSection';

const layoutType = getPlatformType();

// ─── KPI STAT CARD ──────────────────────────────────────────────────────────
const StatCard = ({
  icon,
  label,
  value,
  badge,
  badgeColor,
  badgeTextColor,
  iconBg,
  isMobile,
  onPress,
  onIconPress,
}) => {
  const handlePress = onIconPress || onPress;

  return (
    <View style={[styles.statCard, isMobile && styles.statCardMobile]}>
      <View style={styles.statTop}>
        <TouchableOpacity
          style={[
            styles.statIconWrap,
            { backgroundColor: iconBg || '#F5F3FF' },
            handlePress && { cursor: 'pointer' },
          ]}
          onPress={handlePress}
          disabled={!handlePress}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={label}
        >
          {icon}
        </TouchableOpacity>
        {badge ? (
          <View style={[styles.statBadge, { backgroundColor: badgeColor || '#ECFDF5' }]}>
            <Text style={[styles.statBadgeText, { color: badgeTextColor || '#10B981' }]}>
              {badge}
            </Text>
          </View>
        ) : null}
      </View>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
};


// ─── ACTION REQUIRED ITEM ────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  HIGH: { bg: '#FEE2E2', text: '#EF4444' },
  MEDIUM: { bg: '#FEF3C7', text: '#F59E0B' },
  LOW: { bg: '#E0E7FF', text: '#6366F1' },
};

const ActionItem = ({ item, onResolve }) => {
  const priority = item.priority?.toUpperCase() || 'LOW';
  const pc = PRIORITY_COLORS[priority] || PRIORITY_COLORS.LOW;
  return (
    <View style={styles.actionItem}>
      <View style={[styles.priorityBadge, { backgroundColor: pc.bg }]}>
        <Text style={[styles.priorityText, { color: pc.text }]}>{priority}</Text>
      </View>
      <Text style={styles.actionText} numberOfLines={1}>
        {item.title || item.name}
      </Text>
      <TouchableOpacity
        style={styles.resolveBtn}
        onPress={() => onResolve && onResolve(item)}
        activeOpacity={0.8}
      >
        <Text style={styles.resolveBtnText}>Resolve</Text>
      </TouchableOpacity>
    </View>
  );
};

// ─── UPCOMING EVENT ITEM ─────────────────────────────────────────────────────
const EVENT_COLORS = ['#EF4444', '#6C2BD9', '#10B981', '#F59E0B', '#3B82F6'];
const EventItem = ({ item, index, onPress }) => {
  const color = EVENT_COLORS[index % EVENT_COLORS.length];
  return (
    <TouchableOpacity
      style={styles.eventItem}
      onPress={() => onPress && onPress(item)}
      activeOpacity={0.75}
    >
      <View style={[styles.eventBar, { backgroundColor: color }]} />
      <View style={styles.eventContent}>
        <Text style={styles.eventTitle} numberOfLines={1}>
          {item.title || item.name}
        </Text>
        <Text style={styles.eventSub} numberOfLines={1}>
          {item.date ? new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
          {item.address ? ` • ${item.address}` : ''}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color="#9CA3AF" />
    </TouchableOpacity>
  );
};

// ─── RECENTLY VIEWED PROPERTY CARD ─────────────────────────────────────────
const RecentPropertyCard = ({ item, onPress }) => (
  <TouchableOpacity style={styles.recentCard} onPress={() => onPress(item)} activeOpacity={0.75}>
    <View style={styles.recentIconWrap}>
      <Ionicons name="home-outline" size={18} color="#6C2BD9" />
    </View>
    <View style={{ flex: 1, marginLeft: 10 }}>
      <Text style={styles.recentName} numberOfLines={1}>
        {item?.name || item?.address || 'Property'}
      </Text>
      <Text style={styles.recentSub} numberOfLines={1}>
        {item?.city || item?.postcode || 'Active Property'}
      </Text>
    </View>
    <Ionicons name="chevron-forward" size={16} color="#9CA3AF" />
  </TouchableOpacity>
);

// ─── SECTION CARD WRAPPER ────────────────────────────────────────────────────
const SectionCard = ({ title, badge, onViewAll, children, style }) => (
  <View style={[styles.sectionCard, style]}>
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.sectionHeaderRight}>
        {badge ? (
          <View style={styles.sectionBadge}>
            <Text style={styles.sectionBadgeText}>{badge}</Text>
          </View>
        ) : null}
        {onViewAll ? (
          <TouchableOpacity onPress={onViewAll} activeOpacity={0.7}>
            <Text style={styles.viewAllText}>View all</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
    {children}
  </View>
);

// ─── CHECKLIST STEPS DATA ───────────────────────────────────────────────────
const CHECKLIST_STEPS = [
  { id: 1, label: 'Add Your First Property', sub: 'Add your property profile' },
  { id: 2, label: 'Complete ID Verification', sub: 'Verify your identity documents' },
  { id: 3, label: 'Add Your First Tenancy', sub: 'Link a tenant to your property' },
  { id: 4, label: 'Setup Bank Account', sub: 'Add bank details to receive payouts' },
];


// ─── MAIN HOME DASHBOARD ────────────────────────────────────────────────────
const Home = ({ navigation }) => {
  const focused = useIsFocused();
  const { width } = useWindowDimensions();
  const isMobile = width <= 1024;

  const reduxUser = useSelector((state) => state?.Login?.userDetails);
  const dispatch = useDispatch();

  // Dynamic state from backend APIs
  const [properties, setProperties] = useState([]);
  const [filterProperties, setFilterProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [payments, setPayments] = useState([]);
  const [userProfile, setUserProfile] = useState(reduxUser || null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllDashboardData();
  }, [focused]);

  const fetchAllDashboardData = () => {
    setLoading(true);

    // 1. Fetch Properties
    ajax
      .get(Urls.Properties)
      .then(({ data }) => {
        if (data?.status && Array.isArray(data?.payload)) {
          setProperties(data.payload);
          setFilterProperties(data.payload);
        } else if (Array.isArray(data)) {
          setProperties(data);
          setFilterProperties(data);
        }
      })
      .catch((err) => console.log('Error fetching properties for dashboard', err));

    // 2. Fetch Tenants / Tenancies
    ajax
      .get(Urls.GET_TEANANT_DETAILS)
      .then(({ data }) => {
        if (data?.status && Array.isArray(data?.payload)) {
          setTenants(data.payload);
        }
      })
      .catch((err) => console.log('Error fetching tenants', err));

    // 3. Fetch Reminders / Events for current month
    const m = new Date().getMonth() + 1;
    const y = new Date().getFullYear();
    ajax
      .get(`${Urls.GET_REMAINDERS_MONTHLY}${m}/${y}`)
      .then(({ data }) => {
        if (data?.status && Array.isArray(data?.payload)) {
          setEventsData(data.payload);
        }
      })
      .catch((err) => console.log('Error fetching reminders', err));

    // 4. Fetch Payments for current month
    ajax
      .get(`${Urls.GET_PAYMENTS_MONTHL}month=${m}&year=${y}`)
      .then(({ data }) => {
        if (data?.status && Array.isArray(data?.payload)) {
          setPayments(data.payload);
        }
      })
      .catch((err) => console.log('Error fetching payments', err));

    // 5. Fetch User Profile
    ajax
      .get(Urls.PROFILE)
      .then(({ data }) => {
        if (data?.status) {
          const profile = data?.data?.userDetails || data?.payload;
          if (profile) {
            setUserProfile(profile);
            dispatch(saveUserProfile(profile));
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const _onSearch = (text) => {
    if (!text.trim()) {
      setFilterProperties(properties);
      return;
    }
    ajax
      .post(Urls.SearchProperty, { searchText: text })
      .then(({ data }) => {
        if (data?.status && Array.isArray(data?.data)) {
          setFilterProperties(data.data);
        } else {
          const q = text.toLowerCase();
          setFilterProperties(properties.filter((p) => p.name?.toLowerCase().includes(q)));
        }
      })
      .catch(() => {
        const q = text.toLowerCase();
        setFilterProperties(properties.filter((p) => p.name?.toLowerCase().includes(q)));
      });
  };

  const _onSearchDebounce = useCallback(debounce(_onSearch, 500), [properties]);

  const handleSearchChange = (text) => {
    setSearchText(text);
    _onSearchDebounce(text);
  };

  // Determine user first name
  const userName =
    userProfile?.first_name ||
    userProfile?.name ||
    reduxUser?.first_name ||
    'Landlord';

  // Dynamic Stats calculations
  const totalPropertiesCount = properties.length;
  const activeTenanciesCount = tenants.length;
  const occupancyRate = totalPropertiesCount > 0 ? Math.round((activeTenanciesCount / totalPropertiesCount) * 100) : 0;

  const totalMonthlyIncomeNum = payments
    .filter((p) => p.status === 'PAID' || p.isPaid)
    .reduce((sum, p) => sum + Number(p.amount || p.price || 0), 0);
  const monthlyIncomeText = totalMonthlyIncomeNum > 0 ? `£${totalMonthlyIncomeNum.toLocaleString()}` : '£0.00';

  const totalOutstandingNum = payments
    .filter((p) => p.status === 'UNPAID' || p.isOverdue)
    .reduce((sum, p) => sum + Number(p.amount || p.price || 0), 0);
  const outstandingText = totalOutstandingNum > 0 ? `£${totalOutstandingNum.toLocaleString()}` : '£0.00';

  // Dynamic Action Required items
  const dynamicActions = [];
  if (totalPropertiesCount === 0) {
    dynamicActions.push({ id: 'add-prop', title: 'Add your property profile', priority: 'HIGH', route: 'PropertyAddress' });
  }
  if (totalOutstandingNum > 0) {
    dynamicActions.push({ id: 'overdue-pay', title: 'Overdue rent payment pending', priority: 'HIGH', route: 'Payments' });
  }

  const handleEventPress = (item) => {
    if (item?.type === 'Outgoing') {
      navigation.navigate('PaymentOutgoingDetails', { id: item?.id, type: 'EDIT' });
    } else if (item?.type === 'Incoming') {
      navigation.navigate('PaymentIncomingDetails', { id: item?.id, type: 'EDIT' });
    } else if (item?.id) {
      navigation.navigate('EditReminder', { id: item.id, type: item?.type || item?.remainder_type || 'other' });
    } else {
      navigation.navigate('Reminder');
    }
  };

  // FIRST-TIME USER (FTU) DASHBOARD VIEW
  const isFirstTimeUser = false;

  // Dynamic FTU Checklist progress calculation (starts at 0% when nothing is set up)
  const step1Done = totalPropertiesCount > 0;
  const step2Done = Boolean(userProfile?.is_verified);
  const step3Done = Boolean(userProfile?.has_bank || userProfile?.sort_code);
  const step4Done = activeTenanciesCount > 0;
  const step5Done = false;

  const completedStepsList = [step1Done, step2Done, step3Done, step4Done, step5Done];
  const completedStepsCount = completedStepsList.filter(Boolean).length;
  const setupProgressPct = Math.round((completedStepsCount / 5) * 100);
  const activeStepId = completedStepsList.findIndex((done) => !done) + 1 || 1;

  /* =========================================================================
     1. FIRST-TIME USER (FTU) DASHBOARD VIEW (Matching Figma 1-2 & 189-4)
     ========================================================================= */
  if (isFirstTimeUser) {
    return (
      <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={true}>
        {/* Header */}
        <View style={styles.ftuHeader}>
          <Text style={styles.ftuTitle}>Welcome to Hubita, {userName}! 👋</Text>
          <Text style={styles.ftuSubtitle}>Let's get your portfolio set up. It only takes a few minutes.</Text>
        </View>

        {/* Complete Your Setup Banner Card */}
        <View style={styles.ftuBanner}>
          <View style={styles.ftuBannerContent}>
            <Text style={styles.ftuBannerTitle}>Complete your setup</Text>
            <Text style={styles.ftuBannerSub}>
              {completedStepsCount} of 5 steps completed — you're almost ready to manage your portfolio!
            </Text>
            <View style={styles.progressBarWrap}>
              <View style={[styles.progressBarFill, { width: `${setupProgressPct}%` }]} />
            </View>
            <Text style={styles.ftuBannerPct}>{setupProgressPct}% Complete</Text>

            <TouchableOpacity
              style={styles.ftuContinueBtn}
              onPress={() => navigation.navigate('PropertyAddress')}
              activeOpacity={0.85}
            >
              <Text style={styles.ftuContinueBtnText}>Continue Setup →</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Getting Started Checklist */}
        <View style={styles.cardWrapper}>
          <Text style={styles.cardHeaderTitle}>🚀 Getting Started Checklist</Text>
          <Text style={styles.cardHeaderSub}>Complete these steps to unlock your full dashboard</Text>

          <View style={styles.checklistGroup}>
            {CHECKLIST_STEPS.map((step) => {
              const isDone = completedStepsList[step.id - 1];
              const isActive = step.id === activeStepId;
              return (
                <TouchableOpacity
                  key={step.id}
                  style={styles.checkItemRow}
                  onPress={() => {
                    if (step.id === 1 || step.id === 3) navigation.navigate('PropertyAddress');
                    else if (step.id === 2) navigation.navigate('MyAccount');
                    else navigation.navigate('Tenants');
                  }}
                  activeOpacity={0.8}
                >
                  <View style={[styles.checkCircle, isDone && styles.checkCircleDone, isActive && styles.checkCircleActive]}>
                    {isDone ? (
                      <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                    ) : (
                      <Text style={[styles.checkNum, isActive && { color: '#FFFFFF' }]}>{step.id}</Text>
                    )}
                  </View>

                  <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text style={[styles.checkItemLabel, isDone && styles.checkItemLabelDone]}>{step.label}</Text>
                    <Text style={styles.checkItemSub}>{step.sub}</Text>
                  </View>

                  <View style={[styles.checkPill, isDone && styles.checkPillDone, isActive && styles.checkPillActive]}>
                    <Text style={[styles.checkPillText, isDone && styles.checkPillTextDone, isActive && styles.checkPillTextActive]}>
                      {isDone ? 'Done' : isActive ? 'Active' : 'Upcoming'}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* 3 Empty State Cards Grid */}
        <View style={[styles.emptyGridRow, isMobile && styles.emptyGridRowMobile]}>
          {/* Card 1: Properties */}
          <View style={styles.emptyCardItem}>
            <View style={styles.emptyIconCircle}>
              <Feather name="home" size={20} color="#6C2BD9" />
            </View>
            <Text style={styles.emptyCardTitle}>No Properties Yet</Text>
            <Text style={styles.emptyCardSub}>Add your first property to start managing your portfolio.</Text>
            <TouchableOpacity
              style={styles.emptyPrimaryBtn}
              onPress={() => navigation.navigate('PropertyAddress')}
              activeOpacity={0.85}
            >
              <Text style={styles.emptyPrimaryBtnText}>+ Add Property</Text>
            </TouchableOpacity>
          </View>

          {/* Card 2: Tenancies */}
          <View style={styles.emptyCardItem}>
            <View style={[styles.emptyIconCircle, { backgroundColor: '#E0F2FE' }]}>
              <Feather name="users" size={20} color="#0284C7" />
            </View>
            <Text style={styles.emptyCardTitle}>No Active Tenancies</Text>
            <Text style={styles.emptyCardSub}>Once you add a property, you can create tenancies and invite tenants.</Text>
            <TouchableOpacity
              style={styles.emptySecondaryBtn}
              onPress={() => navigation.navigate('Tenants')}
              activeOpacity={0.85}
            >
              <Text style={styles.emptySecondaryBtnText}>Add Tenancy</Text>
            </TouchableOpacity>
          </View>

          {/* Card 3: Payments */}
          <View style={styles.emptyCardItem}>
            <View style={[styles.emptyIconCircle, { backgroundColor: '#ECFDF5' }]}>
              <Feather name="credit-card" size={20} color="#10B981" />
            </View>
            <Text style={styles.emptyCardTitle}>No Payment Records</Text>
            <Text style={styles.emptyCardSub}>Your rent collection and payment history will appear here.</Text>
            <TouchableOpacity
              style={styles.emptySecondaryBtn}
              onPress={() => navigation.navigate('Payments')}
              activeOpacity={0.85}
            >
              <Text style={styles.emptySecondaryBtnText}>Set Up Payments</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Quick Tips for New Landlords */}
        <View style={styles.cardWrapper}>
          <Text style={styles.cardHeaderTitle}>💡 Quick Tips for New Landlords</Text>
          <Text style={styles.cardHeaderSub}>Get the most out of Hubita</Text>

          <View style={[styles.tipsGridRow, isMobile && styles.tipsGridRowMobile]}>
            <View style={styles.tipBox}>
              <View style={styles.tipIconBox}>
                <Feather name="layers" size={16} color="#6C2BD9" />
              </View>
              <Text style={styles.tipBoxTitle}>Add Multiple Properties</Text>
              <Text style={styles.tipBoxDesc}>Manage your entire portfolio from one central dashboard.</Text>
            </View>

            <View style={styles.tipBox}>
              <View style={styles.tipIconBox}>
                <Feather name="file-text" size={16} color="#6C2BD9" />
              </View>
              <Text style={styles.tipBoxTitle}>Store Documents Safely</Text>
              <Text style={styles.tipBoxDesc}>Upload gas certificates, leases, and deposit records securely.</Text>
            </View>

            <View style={styles.tipBox}>
              <View style={styles.tipIconBox}>
                <Feather name="message-square" size={16} color="#6C2BD9" />
              </View>
              <Text style={styles.tipBoxTitle}>Communicate with Tenants</Text>
              <Text style={styles.tipBoxDesc}>Use the portal to log maintenance requests and send messages directly.</Text>
            </View>
          </View>
        </View>

        {/* Recent Activity (Empty State) */}
        <View style={[styles.cardWrapper, { marginBottom: 40 }]}>
          <Text style={styles.cardHeaderTitle}>Recent Activity</Text>
          <View style={styles.emptyActivityBox}>
            <Text style={styles.emptyActivityText}>No activity yet</Text>
            <Text style={styles.emptyActivitySub}>Your activity feed will update as you manage your properties.</Text>
          </View>
        </View>
      </ScrollView>
    );
  }

  /* =========================================================================
     2. REGULAR DASHBOARD VIEW WITH DYNAMIC DATA (Matching Hubita Dashboard)
     ========================================================================= */
  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={true}>
      {/* Top Header */}
      <View style={[styles.dashboardHeaderRow, isMobile && styles.dashboardHeaderRowMobile]}>
        <View>
          <Text style={styles.dashboardTitle}>Welcome back, {userName} 👋</Text>
          <Text style={styles.dashboardSubtitle}>Here's what's happening with your properties today.</Text>
        </View>
      </View>

      {/* BECOME A VERIFIED LANDLORD SECTION */}
      <VerificationSection isHome={true} userProfile={userProfile} onProfileRefresh={setUserProfile} />

      {/* 4 DYNAMIC KPI STAT CARDS */}
      <View style={[styles.statsGridRow, isMobile && styles.statsGridRowMobile]}>
        <StatCard
          icon={<Ionicons name="home-outline" size={18} color="#6C2BD9" />}
          label="Total Properties"
          value={totalPropertiesCount}
          badge={`+${totalPropertiesCount}`}
          badgeColor="#F5F3FF"
          badgeTextColor="#6C2BD9"
          iconBg="#F5F3FF"
          isMobile={isMobile}
          onPress={() => navigation.navigate('Properties')}
        />

        <StatCard
          icon={<Ionicons name="people-outline" size={18} color="#3B82F6" />}
          label="Active Tenancies"
          value={activeTenanciesCount}
          badge={`${occupancyRate}% Occupancy`}
          badgeColor="#E0F2FE"
          badgeTextColor="#0284C7"
          iconBg="#E0F2FE"
          isMobile={isMobile}
          onPress={() => navigation.navigate('Tenants')}
        />

        {/* <StatCard
          icon={<Ionicons name="card-outline" size={18} color="#10B981" />}
          label="Monthly Income"
          value={monthlyIncomeText}
          // badge="+12%"
          badgeColor="#ECFDF5"
          badgeTextColor="#10B981"
          iconBg="#ECFDF5"
          isMobile={isMobile}
        />

        <StatCard
          icon={<Ionicons name="alert-circle-outline" size={18} color="#EF4444" />}
          label="Outstanding Payments"
          value={outstandingText}
          badge={`${payments.filter((p) => p.status === 'UNPAID').length} overdue`}
          badgeColor="#FEE2E2"
          badgeTextColor="#EF4444"
          iconBg="#FEE2E2"
          isMobile={isMobile}
        /> */}
      </View>

      {/* MAIN TWO-COLUMN DASHBOARD GRID */}
      <View style={[styles.mainGridRow, isMobile && styles.mainGridRowMobile]}>
        {/* LEFT COLUMN */}
        <View style={[styles.gridColLeft, isMobile && styles.gridColLeftMobile]}>
          {/* Dynamic Action Required */}
          <SectionCard
            title="⚠️ Action Required"
            badge={dynamicActions.length > 0 ? `${dynamicActions.length} item${dynamicActions.length === 1 ? '' : 's'}` : null}
            onViewAll={() => navigation.navigate('Properties')}
          >
            {dynamicActions.length > 0 ? (
              dynamicActions.map((item) => (
                <ActionItem
                  key={item.id}
                  item={item}
                  onResolve={() => {
                    if (item.route === 'PropertyAddress') {
                      navigation.push('PropertyAddress');
                    } else {
                      navigation.navigate(item.route || 'Properties');
                    }
                  }}
                />
              ))
            ) : (
              <View style={styles.emptySmallBox}>
                <Text style={styles.emptySmallText}>All portfolio actions up to date.</Text>
              </View>
            )}
          </SectionCard>
        </View>

        {/* RIGHT COLUMN */}
        <View style={[styles.gridColRight, isMobile && styles.gridColRightMobile]}>
          {/* Dynamic Upcoming Events */}
          <SectionCard
            title="📅 Upcoming Events"
            onViewAll={() => navigation.navigate('Reminder')}
          >
            {eventsData.length > 0 ? (
              eventsData.slice(0, 5).map((item, i) => (
                <EventItem key={item.id || i} item={item} index={i} onPress={handleEventPress} />
              ))
            ) : (
              <View style={styles.emptySmallBox}>
                <Text style={styles.emptySmallText}>No upcoming events scheduled for this month.</Text>
              </View>
            )}
          </SectionCard>

          {/* Recently Viewed / Active Properties */}
          {filterProperties.length > 0 && (
            <SectionCard
              title="Recently Viewed Properties"
              style={{ marginTop: 20 }}
              onViewAll={() => navigation.navigate('Properties')}
            >
              {filterProperties.slice(0, 4).map((item) => (
                <RecentPropertyCard
                  key={item.id}
                  item={item}
                  onPress={(prop) => navigation.navigate('PropertyInfo', { id: prop.id })}
                />
              ))}
            </SectionCard>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: Home,
  showBackButton: false,
  noScroll: true,
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  /* DASHBOARD HEADER */
  dashboardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  dashboardHeaderRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  dashboardTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  dashboardSubtitle: {
    fontSize: 13,
    color: '#6B7280',
  },

  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    minWidth: 260,
    ...SHADOWS.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  /* STATS GRID */
  statsGridRow: {
    flexDirection: 'row',
    gap: 14,
    marginBottom: 20,
  },
  statsGridRowMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },

  statCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    ...SHADOWS.sm,
  },
  statCardMobile: {
    width: '48%',
    minWidth: '46%',
    flex: 0,
    flexGrow: 1,
    flexShrink: 0,
  },

  statTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  statIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statBadge: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  statBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  statValue: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
  },

  /* MAIN GRID */
  mainGridRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    marginBottom: 40,
  },
  mainGridRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 16,
    marginBottom: 20,
  },

  gridColLeft: {
    flex: 1.6,
  },
  gridColLeftMobile: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
  },
  gridColRight: {
    flex: 1,
  },
  gridColRightMobile: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
  },

  /* SECTION CARD */
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 18,
    ...SHADOWS.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  sectionHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionBadge: {
    backgroundColor: '#F5F3FF',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  sectionBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  viewAllText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6C2BD9',
  },

  /* ACTION ITEM */
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  priorityBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginRight: 10,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '800',
  },
  actionText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
    marginRight: 10,
  },
  resolveBtn: {
    backgroundColor: '#F5F3FF',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  resolveBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#6C2BD9',
  },

  /* EVENT ITEM */
  eventItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  eventBar: {
    width: 3,
    height: 32,
    borderRadius: 2,
    marginRight: 10,
  },
  eventContent: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  eventSub: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 2,
  },

  /* RECENT PROPERTY CARD */
  recentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  recentIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  recentName: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  recentSub: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 1,
  },

  /* REVENUE OVERVIEW */
  revenueToggleRow: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
    backgroundColor: '#F3F4F6',
    borderRadius: 6,
    padding: 2,
    marginBottom: 12,
  },
  toggleBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  toggleBtnActive: {
    backgroundColor: '#FFFFFF',
    ...SHADOWS.sm,
  },
  toggleBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6B7280',
  },
  toggleBtnTextActive: {
    color: '#6C2BD9',
  },

  chartBox: {
    height: 140,
    backgroundColor: '#FAFAFE',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  chartText: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '600',
  },

  revenueStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
    paddingTop: 12,
  },
  revStatItem: {
    alignItems: 'center',
  },
  revStatLabel: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 2,
  },
  revStatValue: {
    fontSize: 15,
    fontWeight: '800',
  },

  /* ================= FTU DASHBOARD STYLES ================= */
  ftuHeader: {
    marginBottom: 18,
  },
  ftuTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  ftuSubtitle: {
    fontSize: 13,
    color: '#6B7280',
  },

  ftuBanner: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.lg,
    padding: 24,
    marginBottom: 20,
    ...SHADOWS.sm,
  },
  ftuBannerContent: {
    maxWidth: 600,
  },
  ftuBannerTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  ftuBannerSub: {
    fontSize: 13,
    color: '#E9D5FF',
    lineHeight: 18,
    marginBottom: 14,
  },
  progressBarWrap: {
    height: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 6,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 4,
  },
  ftuBannerPct: {
    fontSize: 11,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 16,
  },
  ftuContinueBtn: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    height: 38,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  ftuContinueBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
  },

  /* CARD WRAPPER */
  cardWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 20,
    marginBottom: 20,
    ...SHADOWS.sm,
  },
  cardHeaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },
  cardHeaderSub: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 16,
  },

  checklistGroup: {
    gap: 10,
  },
  checkItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFAFE',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    padding: 12,
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkCircleDone: {
    backgroundColor: '#10B981',
    borderColor: '#10B981',
  },
  checkCircleActive: {
    backgroundColor: '#6C2BD9',
    borderColor: '#6C2BD9',
  },
  checkNum: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B7280',
  },
  checkItemLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  checkItemLabelDone: {
    color: '#10B981',
  },
  checkItemSub: {
    fontSize: 11,
    color: '#6B7280',
    marginTop: 1,
  },
  checkPill: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: '#F3F4F6',
  },
  checkPillDone: {
    backgroundColor: '#ECFDF5',
  },
  checkPillActive: {
    backgroundColor: '#F5F3FF',
  },
  checkPillText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#9CA3AF',
  },
  checkPillTextDone: {
    color: '#10B981',
  },
  checkPillTextActive: {
    color: '#6C2BD9',
  },

  /* EMPTY GRID ROW */
  emptyGridRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
  },
  emptyGridRowMobile: {
    flexDirection: 'column',
    gap: 14,
  },
  emptyCardItem: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 20,
    alignItems: 'center',
    textAlign: 'center',
    ...SHADOWS.sm,
  },
  emptyIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
    textAlign: 'center',
  },
  emptyCardSub: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
    textAlign: 'center',
    marginBottom: 16,
  },
  emptyPrimaryBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyPrimaryBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  emptySecondaryBtn: {
    backgroundColor: '#F5F3FF',
    borderWidth: 1,
    borderColor: '#E0D9FA',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptySecondaryBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#6C2BD9',
  },

  /* TIPS GRID */
  tipsGridRow: {
    flexDirection: 'row',
    gap: 16,
  },
  tipsGridRowMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  tipBox: {
    flex: 1,
    backgroundColor: '#FAFAFE',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    borderRadius: BORDER_RADIUS.md,
    padding: 14,
  },
  tipIconBox: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  tipBoxTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },
  tipBoxDesc: {
    fontSize: 11,
    color: '#6B7280',
    lineHeight: 16,
  },

  emptyActivityBox: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyActivityText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#9CA3AF',
    marginBottom: 2,
  },
  emptyActivitySub: {
    fontSize: 12,
    color: '#D1D5DB',
  },
  emptySmallBox: {
    paddingVertical: 14,
    alignItems: 'center',
  },
  emptySmallText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
});